import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

const SLIDES = [
  {
    id: "slide-1",
    badge: "ABN HOLDERS EXCLUSIVE",
    title: "Brand-new cars for Australian",
    titleAccent: "business owners.",
    subtitle:
      "No credit checks. Operating lease with deferred purchase. Drive away in 24–48 hours with full peace of mind.",
    image:
      "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80&auto=format&fit=crop",
    alt: "Luxury silver sports car parked on a coastal road at sunset",
    align: "left",
  },
  {
    id: "slide-2",
    badge: "FULLY INSURED · ROAD-READY",
    title: "Utes & SUVs handled",
    titleAccent: "end-to-end.",
    subtitle:
      "QBE insurance, registration and comprehensive cover included — one simple weekly payment that keeps you moving.",
    image:
      "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=1920&q=80&auto=format&fit=crop",
    alt: "Modern SUV driving on an open Australian highway",
    align: "left",
  },
  {
    id: "slide-3",
    badge: "NO CREDIT CHECKS REQUIRED",
    title: "Fast pre-approval,",
    titleAccent: "Australia-wide.",
    subtitle:
      "13.5% advance payment. Transparent terms. Empowering self-employed Australians without the traditional bank bureaucracy.",
    image:
      "https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=1920&q=80&auto=format&fit=crop",
    alt: "Business owner receiving car keys from dealer",
    align: "left",
  },
];

const AUTO_ROTATE_MS = 6000;

export default function HeroSlideshow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hover, setHover] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((i) => setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length), []);
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    if (paused || hover) return;
    timerRef.current = setTimeout(() => setIndex((i) => (i + 1) % SLIDES.length), AUTO_ROTATE_MS);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, hover]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const slide = SLIDES[index];

  return (
    <section
      data-testid="hero-slideshow"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative overflow-hidden h-[92vh] min-h-[640px] max-h-[900px] bg-[#050505] text-white"
      aria-roledescription="carousel"
      aria-label="Featured highlights"
    >
      {/* Background image with cross-fade */}
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1 }}
          transition={{ opacity: { duration: 1.1, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
          className="absolute inset-0"
        >
          <img
            src={slide.image}
            alt={slide.alt}
            loading={index === 0 ? "eager" : "lazy"}
            className="w-full h-full object-cover"
          />
          {/* Layered gradient wash */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />
          {/* Fine grain */}
          <div
            className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Foreground content */}
      <div className="relative z-10 h-full">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-full flex items-center pt-20">
          <div className="max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id + "-copy"}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] text-[#D4AF37] mb-6 px-3 py-1.5 border border-[#D4AF37]/30 rounded-full bg-black/40 backdrop-blur-md">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] animate-pulse" />
                  {slide.badge}
                </div>

                <h1
                  className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white"
                  style={{ fontWeight: 300 }}
                >
                  {slide.title}{" "}
                  <span className="italic bg-gradient-to-r from-[#F6ECCC] via-[#D4AF37] to-[#AA820A] bg-clip-text text-transparent">
                    {slide.titleAccent}
                  </span>
                </h1>

                <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
                  {slide.subtitle}
                </p>

                <div className="mt-9 flex flex-wrap gap-3">
                  <Link
                    to="/apply"
                    data-testid="hero-cta-apply"
                    className="group inline-flex items-center gap-2 bg-[#D4AF37] text-[#050505] px-7 py-3.5 text-sm font-semibold hover:bg-[#E5C158] transition-all duration-300 rounded-sm shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                  >
                    Apply Now
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/services#calculator"
                    data-testid="hero-cta-calculator"
                    className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 text-sm font-medium hover:border-[#D4AF37]/60 hover:text-[#D4AF37] hover:bg-white/[0.03] transition-all duration-300 rounded-sm backdrop-blur"
                  >
                    Calculate Repayments
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Arrows */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          data-testid="hero-slide-prev"
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center border border-white/20 rounded-full text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 hover:bg-black/40 backdrop-blur transition"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          data-testid="hero-slide-next"
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center border border-white/20 rounded-full text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 hover:bg-black/40 backdrop-blur transition"
        >
          <ChevronRight size={20} />
        </button>

        {/* Bottom controls: progress + dots + pause */}
        <div className="absolute bottom-8 left-0 right-0 z-10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-end justify-between gap-6">
            {/* Slide index + progress */}
            <div className="flex items-end gap-4 text-white/60 text-xs tracking-widest">
              <div className="font-mono text-white">
                <span className="text-[#D4AF37] text-base">{String(index + 1).padStart(2, "0")}</span>
                <span className="text-white/30"> / {String(SLIDES.length).padStart(2, "0")}</span>
              </div>
            </div>

            {/* Dot indicators */}
            <div className="flex items-center gap-2">
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => goTo(i)}
                  data-testid={`hero-slide-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === index}
                  className="group relative h-1 overflow-hidden"
                  style={{ width: i === index ? 56 : 24 }}
                >
                  <span className="absolute inset-0 bg-white/20" />
                  {i === index && !paused && !hover && (
                    <motion.span
                      key={s.id + "-progress"}
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: AUTO_ROTATE_MS / 1000, ease: "linear" }}
                      className="absolute inset-y-0 left-0 bg-[#D4AF37]"
                    />
                  )}
                  {i === index && (paused || hover) && (
                    <span className="absolute inset-0 bg-[#D4AF37]" />
                  )}
                </button>
              ))}
              <button
                onClick={() => setPaused((p) => !p)}
                data-testid="hero-slide-pause"
                aria-label={paused ? "Play slideshow" : "Pause slideshow"}
                className="ml-3 w-8 h-8 flex items-center justify-center border border-white/20 rounded-full text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 transition"
              >
                {paused ? <Play size={12} /> : <Pause size={12} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
